/**
 * 账本：资金概览、账户余额、分录流水。
 *
 * 全部只读 —— 控制台永远不提供「手工改余额」的入口。余额的真相源是
 * `ledger_entries` 的聚合，直接改 `account_balances` 只会制造漂移。
 * 需要调账请走 adjustment 凭证（另有流程）。
 *
 * 分录按借贷分列渲染，遵循账簿的传统排版：借方红、贷方绿，各占一列，
 * 这样一眼就能看出一笔凭证是不是平的。
 */

import { useState } from 'react';
import { useAsync, usePagedList } from '../lib/hooks.ts';
import { useSession } from '../lib/session.tsx';
import type { Collection } from '../lib/api.ts';
import {
  Badge, Card, CopyId, ErrorBox, Field, Loading, Metric, Select,
} from '../components/ui.tsx';
import { DataTable, Pager, type Column } from '../components/table.tsx';
import { formatMinor } from '../lib/money.ts';
import { formatDateTime } from '../lib/format.ts';
import type { Funds, LedgerAccount, LedgerEntry } from '../lib/types.ts';

const TXN_KINDS = [
  'capture', 'refund', 'chargeback', 'chargeback_reversal', 'fee',
  'settlement', 'payout', 'adjustment', 'reserve_hold', 'reserve_release',
];

const TXN_KIND_LABEL: Readonly<Record<string, string>> = {
  capture: '请款', refund: '退款', chargeback: '拒付', chargeback_reversal: '拒付撤销',
  fee: '手续费', settlement: '结算', payout: '出款', adjustment: '调账',
  reserve_hold: '冻结保证金', reserve_release: '释放保证金',
};

const ACCOUNT_KIND_LABEL: Readonly<Record<string, string>> = {
  pending: '待结算', available: '可用', reserve: '保证金',
  fee_income: '手续费收入', fbo: 'FBO 归集户', receivable: '应收', payable: '应付',
};

export function LedgerPage() {
  const session = useSession();
  const [currency, setCurrency] = useState('USD');
  const [pickedOrg, setPickedOrg] = useState('');

  /*
   * 受分组约束的成员不能查平台自己的账户 —— 那里混着所有分组的钱，服务端会给 404。
   * 他只能看「全部通道都在他范围内」的商户，所以资金与账户两块改为先选一个商户。
   * 空 deps：这份清单与本页的筛选状态无关。
   */
  const scope = useAsync<{ unrestricted: boolean; merchants: readonly { id: string; name: string }[] }>(
    '/v1/ledger/money-scope', {}, [],
  );
  const restricted = scope.data !== null && !scope.data.unrestricted;
  const merchants = scope.data?.merchants ?? [];
  const orgId = restricted ? (pickedOrg !== '' ? pickedOrg : merchants[0]?.id ?? '') : session.me.org.id;

  return (
    <>
      <h1>账本</h1>

      {restricted && (
        <Field
          label="商户"
          hint="你的账号按通道分组收窄：只能看全部通道都在你分组内的商户。平台与代理的账户混着其他分组的钱，不在此列。"
        >
          <Select
            value={orgId}
            onChange={setPickedOrg}
            placeholder={merchants.length === 0 ? '没有可查看的商户' : '选择商户'}
            options={merchants.map((m) => ({ value: m.id, label: m.name }))}
          />
        </Field>
      )}

      {restricted && merchants.length === 0 ? (
        <div className="alert" data-tone="info">
          <span className="alert-title">没有可查看的商户</span>
          <span>你所在的分组里还没有任何商户的全部通道都落在组内。</span>
        </div>
      ) : orgId !== '' && (
        <>
          <FundsCard orgId={orgId} currency={currency} onCurrency={setCurrency} />
          <AccountsCard orgId={orgId} currency={currency} />
        </>
      )}
      <EntriesCard />

      {!session.isPlatform && (
        <div className="alert" data-tone="info">
          <span className="alert-title">试算平衡与漂移检测是平台专属</span>
          <span>那是全局口径的对账指标，在你的范围内没有意义 —— 你看到的是自己（及子树）的账户与流水。</span>
        </div>
      )}
    </>
  );
}

function FundsCard({
  orgId, currency, onCurrency,
}: { orgId: string; currency: string; onCurrency: (value: string) => void }) {
  const funds = useAsync<Funds>(
    '/v1/ledger/funds',
    { query: { org_id: orgId, currency } },
    [currency, orgId],
  );

  return (
    <Card
      title="资金概览"
      sub="待结算会在通道结算周期后转为可用；可提现已扣除在途提现单"
      actions={
        <input
          className="input"
          style={{ width: 88 }}
          value={currency}
          onChange={(event) => { onCurrency(event.target.value.toUpperCase()); }}
        />
      }
    >
      {funds.error !== null ? <ErrorBox error={funds.error} />
        : funds.data === null ? <Loading />
        : (
          <>
            <div className="grid cols-4">
              <Metric label="待结算" value={formatMinor(funds.data.pending, currency)} currency={currency} />
              <Metric label="可用" value={formatMinor(funds.data.available, currency)} currency={currency} />
              <Metric label="保证金" value={formatMinor(funds.data.reserve, currency)} currency={currency} />
              <Metric
                label="可提现"
                value={formatMinor(funds.data.withdrawable.withdrawable, currency)}
                currency={currency}
                tone={funds.data.withdrawable.over_committed ? 'bad' : 'good'}
                note={`在途提现 ${formatMinor(funds.data.withdrawable.pending_payouts, currency)}`}
              />
            </div>
            {funds.data.withdrawable.over_committed && (
              <div className="alert" data-tone="bad" style={{ marginTop: 12 }}>
                <span className="alert-title">在途提现已超过可用余额</span>
                <span>
                  这通常是发起提现后又发生了退款或拒付。在余额补回来之前，
                  这些提现单在审批时会被拒绝 —— 需要先撤销其中一部分。
                </span>
              </div>
            )}
          </>
        )}
    </Card>
  );
}

function AccountsCard({ orgId, currency }: { orgId: string; currency: string }) {
  const accounts = useAsync<Collection<LedgerAccount>>(
    '/v1/ledger/accounts',
    { query: { org_id: orgId, currency } },
    [currency, orgId],
  );

  const columns: readonly Column<LedgerAccount>[] = [
    { key: 'kind', header: '账户类型', render: (row) => (
      <div className="stack tight">
        <span className="cell-strong">{ACCOUNT_KIND_LABEL[row.kind] ?? row.kind}</span>
        <CopyId id={row.account_id} />
      </div>
    ) },
    { key: 'currency', header: '币种', render: (row) => row.currency },
    { key: 'natural', header: '余额（自然方向）', numeric: true, render: (row) => (
      <span className="num cell-strong">{formatMinor(row.natural, row.currency)}</span>
    ) },
    { key: 'signed', header: '余额（借正贷负）', numeric: true, render: (row) => (
      <span className="num cell-muted">{formatMinor(row.signed, row.currency)}</span>
    ) },
    { key: 'version', header: '版本', numeric: true, render: (row) => (
      <span className="num cell-muted" title="乐观锁版本号，每次记账 +1">{row.version}</span>
    ) },
  ];

  return (
    <Card title="账户余额" sub="「自然方向」是该账户类型的正常余额方向，看这一列即可" flush>
      <DataTable
        columns={columns}
        rows={accounts.data?.data ?? []}
        loading={accounts.loading}
        error={accounts.error}
        rowKey={(row) => row.account_id}
        empty={`${currency} 还没有任何账户`}
        emptyHint="账户在第一笔相关记账发生时才会被创建。"
      />
    </Card>
  );
}

function EntriesCard() {
  const [kind, setKind] = useState('');
  const [currency, setCurrency] = useState('');

  const list = usePagedList<LedgerEntry>('/v1/ledger/entries', {
    kind: kind === '' ? null : kind,
    currency: currency === '' ? null : currency.toUpperCase(),
  }, 30);

  const columns: readonly Column<LedgerEntry>[] = [
    { key: 'time', header: '发生时间', render: (row) => (
      <span className="cell-muted mono">{formatDateTime(row.occurred_at)}</span>
    ) },
    { key: 'txn', header: '凭证', render: (row) => (
      <div className="stack tight">
        <Badge tone="neutral">{TXN_KIND_LABEL[row.txn_kind] ?? row.txn_kind}</Badge>
        <CopyId id={row.txn_id} />
      </div>
    ) },
    { key: 'account', header: '账户', render: (row) => (
      <div className="stack tight">
        <span>{ACCOUNT_KIND_LABEL[row.kind] ?? row.kind}</span>
        <CopyId id={row.owner_org_id} />
      </div>
    ) },
    // 借贷分列 —— 账簿的核心排版，一眼能看出方向
    { key: 'dr', header: '借方', numeric: true, render: (row) => (
      row.direction === 'dr'
        ? <span className="num dr">{formatMinor(row.amount, row.currency)}</span>
        : <span className="cell-muted">—</span>
    ) },
    { key: 'cr', header: '贷方', numeric: true, render: (row) => (
      row.direction === 'cr'
        ? <span className="num cr">{formatMinor(row.amount, row.currency)}</span>
        : <span className="cell-muted">—</span>
    ) },
    { key: 'currency', header: '币种', render: (row) => row.currency },
    { key: 'ref', header: '关联对象', render: (row) => (
      <div className="stack tight">
        <span className="cell-muted" style={{ fontSize: 11 }}>{row.ref_type}</span>
        <CopyId id={row.ref_id} />
      </div>
    ) },
    { key: 'memo', header: '摘要', render: (row) => (
      <span className="cell-muted cell-wrap">{row.memo ?? '—'}</span>
    ) },
  ];

  return (
    <Card
      title="分录流水"
      sub="每一笔凭证的借贷双方都会各出现一行，两边金额必然相等"
      flush
      actions={
        <div className="row tight">
          <Select
            value={kind}
            onChange={setKind}
            placeholder="全部凭证类型"
            options={TXN_KINDS.map((value) => ({ value, label: TXN_KIND_LABEL[value] ?? value }))}
          />
          <input
            className="input"
            style={{ width: 88 }}
            placeholder="币种"
            value={currency}
            onChange={(event) => { setCurrency(event.target.value.toUpperCase()); }}
          />
        </div>
      }
    >
      <DataTable
        columns={columns}
        rows={list.items}
        loading={list.loading}
        error={list.error}
        rowKey={(row) => row.id}
        empty="还没有任何分录"
        emptyHint="第一笔请款成功后，账本才会开始有记录。"
      />
      <Pager
        page={list.page}
        hasNext={list.hasNext}
        hasPrev={list.hasPrev}
        onNext={list.next}
        onPrev={list.prev}
        loading={list.loading}
        count={list.items.length}
      />
    </Card>
  );
}
